import { z } from "zod";
import { preprocessNullableNumber } from "utils/validation/zod-preprocessors";

import { TREATMENT_ALLOCATION_NAME_MAX_LENGTH } from "./allocation-constants";

// validation schemas for treatment allocation fields
export const TreatmentValidation = {
  name: z
    .string()
    .min(1, "Treatment name is required")
    .max(
      TREATMENT_ALLOCATION_NAME_MAX_LENGTH,
      `Treatment name cannot exceed ${TREATMENT_ALLOCATION_NAME_MAX_LENGTH} characters`,
    ),

  unit_price: z.preprocess(
    preprocessNullableNumber,
    z
      .number({
        error: "Unit price must be a number",
      })
      .min(0, "Unit price cannot be negative")
      .max(999999, "Unit price cannot exceed $999,999")
      .nullable()
      .or(z.literal(null)),
  ),

  units_per_year: z.preprocess(
    preprocessNullableNumber,
    z
      .number()
      .int("Units per year must be a whole number")
      .min(0, "Units per year cannot be negative")
      .max(9999, "Units per year cannot exceed 9999")
      .nullable()
      .or(z.literal(null)),
  ),

  number_of_years: z.preprocess(
    preprocessNullableNumber,
    z
      .number()
      .int("Number of years must be a whole number")
      .min(0, "Number of years cannot be negative")
      .max(99, "Number of years cannot exceed 99")
      .nullable()
      .or(z.literal(null)),
  ),

  exposed_unit_price: z.preprocess(
    preprocessNullableNumber,
    z
      .number()
      .min(0, "Exposed unit price cannot be negative")
      .max(999999, "Exposed unit price cannot exceed $999,999")
      .nullable()
      .or(z.literal(null)),
  ),

  exposed_units_per_year: z.preprocess(
    preprocessNullableNumber,
    z
      .number()
      .int("Exposed units per year must be a whole number")
      .min(0, "Exposed units per year cannot be negative")
      .max(9999, "Exposed units per year cannot exceed 9999")
      .nullable()
      .or(z.literal(null)),
  ),

  number_of_years_exposed: z.preprocess(
    preprocessNullableNumber,
    z
      .number()
      .int("Exposed years must be a whole number")
      .min(0, "Exposed years cannot be negative")
      .max(99, "Exposed years cannot exceed 99")
      .nullable()
      .or(z.literal(null)),
  ),

  mitigated_unit_price: z.preprocess(
    preprocessNullableNumber,
    z
      .number()
      .min(0, "Mitigated unit price cannot be negative")
      .max(999999, "Mitigated unit price cannot exceed $999,999")
      .nullable()
      .or(z.literal(null)),
  ),

  mitigated_units_per_year: z.preprocess(
    preprocessNullableNumber,
    z
      .number()
      .int("Mitigated units per year must be a whole number")
      .min(0, "Mitigated units per year cannot be negative")
      .max(9999, "Mitigated units per year cannot exceed 9999")
      .nullable()
      .or(z.literal(null)),
  ),

  number_of_years_mitigated: z.preprocess(
    preprocessNullableNumber,
    z
      .number()
      .int("Mitigated years must be a whole number")
      .min(0, "Mitigated years cannot be negative")
      .max(99, "Mitigated years cannot exceed 99")
      .nullable()
      .or(z.literal(null)),
  ),

  probability_of_success: z.preprocess(
    preprocessNullableNumber,
    z
      .number()
      .min(0, "Probability must be at least 0")
      .max(100, "Probability cannot exceed 100")
      .nullable()
      .or(z.literal(null)),
  ),
};

// validation schemas for prescription allocation fields
export const PrescriptionValidation = {
  name: z
    .string()
    .min(1, "Drug name is required")
    .max(40, "Drug name cannot exceed 40 characters"),

  ndc: z
    .string()
    .min(1, "NDC is required")
    .max(15, "NDC cannot exceed 15 characters"),

  dosage: z.string().max(40, "Dosage cannot exceed 40 characters"),

  active_ingredient: z
    .string()
    .max(40, "Active ingredient cannot exceed 40 characters"),

  formulation: z.string().max(20, "Formulation cannot exceed 20 characters"),

  unit_price: z.preprocess(
    preprocessNullableNumber,
    z
      .number()
      .min(0, "Unit price cannot be negative")
      .max(9999.9999, "Unit price cannot exceed $9,999.9999")
      .nullable()
      .or(z.literal(null)),
  ),

  units_per_month: z.preprocess(
    preprocessNullableNumber,
    z
      .number()
      .int("Units per month must be a whole number")
      .min(0, "Units per month cannot be negative")
      .max(9999, "Units per month cannot exceed 9999")
      .nullable()
      .or(z.literal(null)),
  ),

  units_per_year: z.preprocess(
    preprocessNullableNumber,
    z
      .number()
      .int("Months per year must be a whole number")
      .min(1, "Months per year must be at least 1")
      .max(12, "Months per year cannot exceed 12")
      .nullable()
      .or(z.literal(null)),
  ),

  exposed_units_per_year: z.preprocess(
    preprocessNullableNumber,
    z
      .number()
      .int("Exposed months per year must be a whole number")
      .min(1, "Exposed months per year must be at least 1")
      .max(12, "Exposed months per year cannot exceed 12")
      .nullable()
      .or(z.literal(null)),
  ),

  mitigated_units_per_year: z.preprocess(
    preprocessNullableNumber,
    z
      .number()
      .int("Mitigated months per year must be a whole number")
      .min(1, "Mitigated months per year must be at least 1")
      .max(12, "Mitigated months per year cannot exceed 12")
      .nullable()
      .or(z.literal(null)),
  ),

  number_of_years: z.preprocess(
    preprocessNullableNumber,
    z
      .number()
      .int("Number of years must be a whole number")
      .min(0, "Number of years cannot be negative")
      .max(99, "Number of years cannot exceed 99")
      .nullable()
      .or(z.literal(null)),
  ),

  probability_of_success: z.preprocess(
    preprocessNullableNumber,
    z
      .number()
      .min(0, "Probability must be at least 0")
      .max(1, "Probability cannot exceed 1")
      .nullable()
      .or(z.literal(null)),
  ),

  exposed_unit_price: z.preprocess(
    preprocessNullableNumber,
    z
      .number()
      .min(0, "Exposed unit price cannot be negative")
      .max(9999.9999, "Exposed unit price cannot exceed $9,999.9999")
      .nullable()
      .or(z.literal(null)),
  ),

  exposed_units_per_month: z.preprocess(
    preprocessNullableNumber,
    z
      .number()
      .int("Exposed units per month must be a whole number")
      .min(0, "Exposed units per month cannot be negative")
      .max(9999, "Exposed units per month cannot exceed 9999")
      .nullable()
      .or(z.literal(null)),
  ),

  exposed_dosage: z
    .string()
    .max(40, "Exposed dosage cannot exceed 40 characters"),

  number_of_years_exposed: z.preprocess(
    preprocessNullableNumber,
    z
      .number()
      .int("Exposed years must be a whole number")
      .min(0, "Exposed years cannot be negative")
      .max(99, "Exposed years cannot exceed 99")
      .nullable()
      .or(z.literal(null)),
  ),

  mitigated_unit_price: z.preprocess(
    preprocessNullableNumber,
    z
      .number()
      .min(0, "Mitigated unit price cannot be negative")
      .max(9999.9999, "Mitigated unit price cannot exceed $9,999.9999")
      .nullable()
      .or(z.literal(null)),
  ),

  mitigated_units_per_month: z.preprocess(
    preprocessNullableNumber,
    z
      .number()
      .int("Mitigated units per month must be a whole number")
      .min(0, "Mitigated units per month cannot be negative")
      .max(9999, "Mitigated units per month cannot exceed 9999")
      .nullable()
      .or(z.literal(null)),
  ),

  mitigated_dosage: z
    .string()
    .max(40, "Mitigated dosage cannot exceed 40 characters"),

  number_of_years_mitigated: z.preprocess(
    preprocessNullableNumber,
    z
      .number()
      .int("Mitigated years must be a whole number")
      .min(0, "Mitigated years cannot be negative")
      .max(99, "Mitigated years cannot exceed 99")
      .nullable()
      .or(z.literal(null)),
  ),
};

// field name to schema mapping for treatment validation
const TreatmentFieldMap: Record<string, z.ZodTypeAny> = {
  name: TreatmentValidation.name,
  unit_price: TreatmentValidation.unit_price,
  exposed_unit_price: TreatmentValidation.exposed_unit_price,
  mitigated_unit_price: TreatmentValidation.mitigated_unit_price,
  units_per_year: TreatmentValidation.units_per_year,
  exposed_units_per_year: TreatmentValidation.exposed_units_per_year,
  mitigated_units_per_year: TreatmentValidation.mitigated_units_per_year,
  number_of_years: TreatmentValidation.number_of_years,
  number_of_years_exposed: TreatmentValidation.number_of_years_exposed,
  number_of_years_mitigated: TreatmentValidation.number_of_years_mitigated,
  probability_of_success: TreatmentValidation.probability_of_success,
};

// field name to schema mapping for prescription validation
const PrescriptionFieldMap: Record<string, z.ZodTypeAny> = {
  name: PrescriptionValidation.name,
  ndc: PrescriptionValidation.ndc,
  dosage: PrescriptionValidation.dosage,
  exposed_dosage: PrescriptionValidation.exposed_dosage,
  mitigated_dosage: PrescriptionValidation.mitigated_dosage,
  active_ingredient: PrescriptionValidation.active_ingredient,
  formulation: PrescriptionValidation.formulation,
  unit_price: PrescriptionValidation.unit_price,
  exposed_unit_price: PrescriptionValidation.exposed_unit_price,
  mitigated_unit_price: PrescriptionValidation.mitigated_unit_price,
  units_per_month: PrescriptionValidation.units_per_month,
  units_per_year: PrescriptionValidation.units_per_year,
  exposed_units_per_month: PrescriptionValidation.exposed_units_per_month,
  mitigated_units_per_month: PrescriptionValidation.mitigated_units_per_month,
  exposed_units_per_year: PrescriptionValidation.exposed_units_per_year,
  mitigated_units_per_year: PrescriptionValidation.mitigated_units_per_year,
  number_of_years: PrescriptionValidation.number_of_years,
  number_of_years_exposed: PrescriptionValidation.number_of_years_exposed,
  number_of_years_mitigated: PrescriptionValidation.number_of_years_mitigated,
  probability_of_success: PrescriptionValidation.probability_of_success,
};

// generic validation function - returns error message or null
const validateField = (
  field: string,
  value: unknown,
  fieldMap: Record<string, z.ZodTypeAny>,
): string | null => {
  const schema = fieldMap[field];
  if (!schema) return null;

  try {
    schema.parse(value);
    return null;
  } catch (error) {
    if (error instanceof z.ZodError) {
      return error.issues[0].message;
    }
    console.error("Validation error:", error);
    return "Please enter a valid value";
  }
};

// validation function for treatment fields - returns error message or null
export const validateTreatmentField = (
  field: string,
  value: unknown,
): string | null => {
  return validateField(field, value, TreatmentFieldMap);
};

// validation function for prescription fields - returns error message or null
export const validatePrescriptionField = (
  field: string,
  value: unknown,
): string | null => {
  return validateField(field, value, PrescriptionFieldMap);
};

// validation for probability percentage input (0-100 range)
// this is used in the UI before converting to 0-1 range for the backend
export const ProbabilityPercentageValidation = z.preprocess(
  preprocessNullableNumber,
  z
    .number({
      error: "Probability must be a number",
    })
    .min(0, "Probability must be at least 0%")
    .max(100, "Probability cannot exceed 100%"),
);

// validation function for probability percentage input (0-100 range)
export const validateProbabilityPercentage = (
  value: unknown,
): string | null => {
  try {
    ProbabilityPercentageValidation.parse(value);
    return null;
  } catch (error) {
    if (error instanceof z.ZodError) {
      return error.issues[0].message;
    }
    console.error("Validation error:", error);
    return "Please enter a valid percentage (0-100)";
  }
};
